<template>
  <div>
    <div class="icons-container">
      <v-btn
        v-if="chartOptions && clickedDate"
        class="today-button"
        x-small
        outlined
        @click="resetSelectedDate"
        >Today</v-btn
      >

      <v-icon
        v-if="showExportIcon && chartOptions"
        color="#aaa"
        size="14"
        class="export-icon"
        title="Export Chart to Image"
        @click="exportChart"
        >fas fa-file-export</v-icon
      >

      <v-tooltip bottom v-if="chartOptions && !clickedDate">
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            v-bind="attrs"
            v-on="on"
            color="blue"
            size="14"
            class="info-icon"
            >fas fa-info-circle</v-icon
          >
        </template>
        <span>Click on the chart line for historical date</span>
      </v-tooltip>

      <v-icon
        v-if="chartOptions"
        color="#aaa"
        size="14"
        class="reload-icon"
        title="Reload Chart"
        @click="reloadChart"
        >fas fa-redo</v-icon
      >
    </div>
    <v-chart
      v-if="chartOptions"
      ref="chartInstance"
      @finished="chartFinished"
      :option="chartOptions"
      :style="sizeCSS"
      @click="chartOnClick"
    />
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashMax from "lodash/max";
import lodashMin from "lodash/min";
import lodashCloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  VisualMapComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkPointComponent,
  ToolboxComponent,
  LegendComponent,
  TitleComponent,
} from "echarts/components";

import { LineChart } from "echarts/charts";

use([
  CanvasRenderer,
  GridComponent,
  ToolboxComponent,
  LegendComponent,
  TitleComponent,
  VisualMapComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkPointComponent,
  LineChart,
]);

import { GRIDS_SCORE_RANGE_DEFAULTS_STABILITY } from "@/common/constants/grids.js";
import { todayUtc } from "@/common/date.utilities";

const EMITTED_ACTIONS = {
  CLICK: "click",
  FINISHED: "finished",
};

export default {
  name: "GridChart",
  props: {
    type: {
      type: String,
      validator: function (value) {
        return ["stability", "hotspots"].indexOf(value) !== -1;
      },
    },
    histogram: {
      type: Array,
      default: () => [],
    },
    stats: {
      type: Object,
    },
    trend: {
      type: Array,
    },
    forecast: {
      type: Array,
    },
    clickClear: {
      type: Date,
    },
    timeframe: {
      type: Number,
      default: 7,
    },
    lockLinePieces: {
      type: Boolean,
      default: false,
    },
    refresh: {
      type: String,
    },
    showExportIcon: {
      type: Boolean,
      default: false,
    },
    preclickedDate: {
      type: Object,
      default: null,
      validator: (d) => {
        return d === null || dayjs.isDayjs(d);
      },
    },
  },
  data: function () {
    return {
      selectedTimeframe: this.timeframe,
      bins: [],
      counts: [],
      countsTrend: [],
      chartBins: [],
      chartCounts: [],
      chartCountsTrend: [],
      pieces: [],
      forecastBins: [],
      forecastCounts: [],
      forecastCountsTrend: [],
      clickedDate: null,
      clickedValue: null,
      lastClickCleared: null,
      imgSrc: null,
    };
  },
  computed: {
    todaysColor() {
      if (this.chartCounts.length) {
        const VALUE = this.chartCounts[
          this.chartCounts.length - this.forecastCounts.length - 1
        ];
        return this.getColor(VALUE);
      }
      return null;
    },
    highValue() {
      if (this.type === "stability") {
        if (this.chartCounts.length) {
          return lodashMax(this.chartCounts).toFixed(2);
        }
      }
      return null;
    },
    lowValue() {
      if (this.type === "stability") {
        if (this.chartCounts.length) {
          return lodashMin(this.chartCounts).toFixed(2);
        }
      }
      return null;
    },
    chartOptions() {
      if (
        this.chartBins &&
        Array.isArray(this.chartBins) &&
        this.chartBins.length &&
        this.chartCounts &&
        Array.isArray(this.chartCounts) &&
        this.chartCounts.length
      ) {
        if (
          this.type === "stability" &&
          (!this.pieces ||
            !Array.isArray(this.pieces) ||
            !this.pieces.length ||
            !this.stats)
        ) {
          return null;
        }

        let options = this.getChartBasics();

        if (this.type === "stability") {
          this.getChartVisualMap(options);
        }

        this.getChartDataSeries(options);
        this.getChartDataSeriesTrend(options);

        if (this.type === "stability" && this.forecast) {
          this.getChartForecastUncertaintySeries(options);
        }

        return options;
      }
      return null;
    },
    xAxis() {
      return this.chartData?.xAxis?.map((x) => x);
    },
    sizeCSS() {
      return `height: ${this.height}; width: 100%; margin-left: 10px;`;
    },
    height() {
      if (window.innerHeight < 950) {
        return "150px;";
      } else {
        return "230px";
      }
    },
  },
  mounted() {
    this.setUpChart();
    window.addEventListener("resize", this.handleResize);
  },
  methods: {
    setUpChart() {
      if (this.histogram) {
        this.setClickedValue();
        this.getBinsAndCounts();
        this.changeChartTimeframe(this.selectedTimeframe);
      }
    },
    setClickedValue() {
      if (this.preclickedDate) {
        let formatted = this.formatClickedDate(this.preclickedDate);
        if (!this.clickedDate?.includes(formatted)) {
          this.clickedDate = formatted;
        }
      }

      if (this.clickedDate) {
        const match = this.histogram.find((item) => {
          return item.key_as_string.indexOf(this.clickedDate) > -1;
        });

        this.clickedValue = match?.count_sum?.value || null;
      }
    },
    chartFinished() {
      this.$emit(EMITTED_ACTIONS.FINISHED, true);

      if (this.$refs?.chartInstance?.chart) {
        const zr = this.$refs.chartInstance.chart.getZr();
        zr.on("click", this.chartOnClick);
      }

      this.reloadChart();
    },
    changeChartTimeframe(timeframe) {
      this.selectedTimeframe = timeframe;
      const BINS = lodashCloneDeep(this.bins);
      const COUNTS = lodashCloneDeep(this.counts);
      const COUNTS_TREND = lodashCloneDeep(this.countsTrend);
      this.chartBins = BINS.splice((timeframe + this.forecastBins.length) * -1);
      this.chartCounts = COUNTS.splice(
        (timeframe + this.forecastCounts.length) * -1
      );
      this.chartCountsTrend = COUNTS_TREND.splice(
        (timeframe + this.forecastCountsTrend.length) * -1
      );
    },
    getColor(value) {
      let color = "#979797";
      if (this.type === "stability") {
        if (
          this.pieces &&
          this.pieces.length &&
          this.counts &&
          this.counts.length
        ) {
          for (let i = 0; i < this.pieces.length; i++) {
            const PIECE = this.pieces[i];
            const MIN = PIECE.min ? parseFloat(PIECE.min) : -1000;
            const MAX = PIECE.max ? parseFloat(PIECE.max) : 1000;
            if (value >= MIN && value < MAX) {
              color = PIECE.color;
              break;
            }
          }
        }
      } else {
        color = "red";
      }
      return color;
    },
    getBinsAndCounts() {
      if (this.histogram) {
        let bins = [];
        let counts = [];
        let countsTrend = [];
        this.histogram.forEach((item) => {
          if (bins.indexOf(item.key_as_string) === -1) {
            const DATE_PARTS = item.key_as_string.split("T");
            const DATE = DATE_PARTS[0];
            bins.push(DATE);
          }
          if (this.type === "stability") {
            if (typeof item === "number") {
              counts.push(item);
            } else {
              counts.push(item.count_sum.value);
            }
          } else {
            counts.push(item.activity.count);
          }
        });

        this.bins = bins;
        this.counts = counts;

        if (this.trend) {
          this.trend.forEach((item) => {
            if (typeof item === "number") {
              countsTrend.push(item);
            } else {
              countsTrend.push(item.count_sum.value);
            }
          });
          this.countsTrend = countsTrend;
        }

        if (this.forecast) {
          this.forecastBins = [];
          this.forecastCounts = [];
          this.forecast.forEach((day) => {
            const DATE_PARTS = day.startTime.split("T");
            const DATE = DATE_PARTS[0];
            this.forecastBins.push(DATE);
            this.forecastCounts.push(day.mean.score);
          });
          this.bins = this.bins.concat(this.forecastBins);
          this.counts = this.counts.concat(this.forecastCounts);
        }
      }
    },
    getLinePieces() {
      if (!this.lockLinePieces) {
        this.pieces = GRIDS_SCORE_RANGE_DEFAULTS_STABILITY;
      }
    },
    getChartBasics() {
      return {
        backgroundColor: "rgba(0, 0, 0, 0)",
        color: ["white"],
        tooltip: {
          textStyle: {
            color: "#FFF",
          },
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          borderWidth: 0,
          backgroundColor: "rgba(3,3,3,0.7)",
          formatter: (params) => {
            const VALUE = Math.floor(params[0].value);
            const DATE = dayjs(params[0].name);
            const isForecast = dayjs(DATE).isAfter(todayUtc());
            const COLOR = this.getColor(VALUE);
            const DOT = `
              <span style="display:inline-block;border-radius:10px;width:10px;height:10px;background-color:${COLOR};"></span>
            `;
            return `
              <span class="pr-1">${isForecast ? "" : VALUE}</span>
              <span class="pr-1">${DOT}</span>
              <span class="pr-1">${this.formatDate(DATE)}</span>
            `;
          },
        },
        legend: {
          show: false,
        },
        title: {
          show: false,
        },
        grid: {
          left: 30,
          right: "5%",
          bottom: "15%",
          top: "10%",
        },
        animation: true,
        xAxis: {
          data: this.chartBins,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: true,
            color: "white",
            interval: (index, value) => {
              return (
                index === 0 ||
                index + 1 === this.histogram.length ||
                index === Math.floor(this.histogram.length / 2)
              );
            },
            formatter: (value) => this.formatDate(value),
            inside: false,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          min:
            parseInt(this.lowValue) === parseInt(this.highValue)
              ? parseInt(this.lowValue) - 1
              : parseInt(this.highValue) +
                (parseInt(this.highValue) - parseInt(this.lowValue)) * 0.4,
          max:
            parseInt(this.lowValue) === parseInt(this.highValue)
              ? parseInt(this.highValue) + 1
              : parseInt(this.lowValue) -
                (parseInt(this.highValue) - parseInt(this.lowValue)) * 0.4,
        },
        visualMap: [],
        series: [],
      };
    },
    getChartVisualMap(options) {
      const VISUAL_MAP = {
        type: "piecewise",
        seriesIndex: 0,
        show: false,
        pieces: this.pieces,
        outOfRange: {
          color: "#979797",
        },
      };
      options.visualMap.push(VISUAL_MAP);
    },
    getChartForecastUncertaintySeries(options) {
      const TODAY_DATE = this.chartBins[
        this.chartBins.length - this.forecastBins.length - 1
      ];
      const TODAY_VALUE = this.chartCounts[
        this.chartCounts.length - this.forecastCounts.length - 1
      ];

      // from echarts.js example:
      // https://echarts.apache.org/examples/en/editor.html?c=confidence-band
      const dataLower = this.chartCounts.map((val, index) => {
        if (index > this.chartCounts.length - 9) {
          const forecastDate = `${this.chartBins[index]}T00:00:00.000Z`;
          let score = TODAY_VALUE;
          if (this.chartBins[index] !== TODAY_DATE) {
            const item = lodashFind(this.forecast, { startTime: forecastDate });
            score = lodashGet(item, "lower.score") ? item.lower.score : null;
          }
          return score;
        }
        return null;
      });
      const seriesLower = {
        name: "Lower",
        type: "line",
        data: dataLower,
        lineStyle: {
          opacity: 1,
          color: "#bbb",
        },
        stack: "uncertainty-band",
        symbol: "none",
      };
      options.series.push(seriesLower);

      const dataUpper = this.chartCounts.map((val, index) => {
        if (index > this.chartCounts.length - 9) {
          const forecastDate = `${this.chartBins[index]}T00:00:00.000Z`;
          let score = 0;
          if (this.chartBins[index] !== TODAY_DATE) {
            const item = lodashFind(this.forecast, { startTime: forecastDate });
            score = lodashGet(item, "upper.score")
              ? item.upper.score - item.lower.score
              : null;
          }
          return score;
        }
        return null;
      });
      const seriesUpper = {
        name: "Upper",
        type: "line",
        data: dataUpper,
        lineStyle: {
          opacity: 1,
          color: "#bbb",
        },
        areaStyle: {
          opacity: 0.3,
          color: "#ccc",
        },
        stack: "uncertainty-band",
        symbol: "none",
      };
      options.series.push(seriesUpper);
    },
    getChartDataSeries(options) {
      let series = {
        type: "line",
        name: "Score",
        data: this.chartCounts,
        itemStyle: {
          opacity: 0,
        },
        lineStyle: {
          width: 2,
        },
      };

      let data = [];
      let itemStyle;

      if (this.type === "hotspots") {
        const HISTOGRAM = lodashCloneDeep(this.histogram);
        const BINS = HISTOGRAM.splice(this.selectedTimeframe * -1);
        BINS.forEach((item) => {
          const DATE_PARTS = item.key_as_string.split("T");
          const DATE = DATE_PARTS[0];
          if (item.hotspot) {
            const COLOR = this.getColor(item.hotspot);
            data.push({
              xAxis: DATE,
              yAxis: item.activity.count,
              itemStyle: {
                color: COLOR,
              },
            });
          }
        });
      }

      const TODAY_DATE = this.chartBins[
        this.chartBins.length - this.forecastBins.length - 1
      ];
      const TODAY_VALUE = this.chartCounts[
        this.chartCounts.length - this.forecastCounts.length - 1
      ];

      if (this.type === "stability") {
        itemStyle = {
          color: this.todaysColor,
          shadowColor: this.todaysColor,
          shadowBlur: 20,
        };
        data = [
          {
            xAxis: TODAY_DATE,
            yAxis: TODAY_VALUE,
          },
        ];
      }

      series.markLine = {
        symbol: ["circle", null],
        silent: true,
        animate: false,
        lineStyle: {
          width: 0,
        },
        label: {
          position: "start",
          formatter: function (thing) {
            return Math.round(thing.value);
          },
        },
        data: [
          {
            type: "min",
            lineStyle: { color: this.getColor(this.stats.min) },
            label: {
              color: this.getColor(this.stats.min),
            },
          },
          {
            type: "max",
            lineStyle: { color: this.getColor(this.stats.max) },
            label: {
              color: this.getColor(this.stats.min),
            },
          },
        ],
      };

      series.markPoint = {
        symbol: "circle",
        symbolSize: 8,
        silent: true,
      };
      series.markPoint.data = data;

      if (itemStyle) {
        series.markPoint.itemStyle = itemStyle;
      }

      if (this.clickedDate && this.clickedValue) {
        const DATE = this.formatDate(this.clickedDate);
        series.markPoint.data.push({
          xAxis: this.clickedDate,
          yAxis: this.clickedValue,
          label: {
            formatter: DATE,
            color: "white",
            position: "insideTop",
            distance: 15,
          },
          itemStyle: {
            color: "white",
            shadowColor: "white",
            shadowBlur: 20,
          },
        });
      }

      options.series.push(series);

      if (this.type === "stability" && this.forecast) {
        const seriesForForecast = {
          type: "line",
          name: "ForecasetLine",
          data: [0, 0],
          itemStyle: {
            opacity: 0,
          },
          markLine: {
            symbol: "none",
            silent: true,
            data: [
              {
                name: "Current Date",
                xAxis: TODAY_DATE + "",
                label: {
                  color: "#fff",
                  formatter: "Forecast",
                  position: "insideStartBottom",
                },
                symbol: "none",
              },
            ],
            lineStyle: {
              type: "dashed",
              color: "#979797",
            },
          },
        };
        options.series.push(seriesForForecast);
      }
    },
    getChartDataSeriesTrend(options) {
      let series = {
        type: "line",
        name: "Trend",
        data: this.chartCountsTrend,
        itemStyle: {
          opacity: 0,
        },
        lineStyle: {
          type: "dotted",
          color: "#979797",
        },
      };
      options.series.push(series);
    },
    chartOnClick(params) {
      //Get the x,y position where the click happened
      const pointInPixel = [params.offsetX, params.offsetY];
      //Get the x axis value at this point
      const pointInGrid = this.$refs.chartInstance.chart.convertFromPixel(
        "grid",
        pointInPixel
      );
      if (
        this.$refs.chartInstance.chart.containPixel("grid", pointInPixel) &&
        this.chartBins.length >= pointInGrid[0]
      ) {
        this.clickedDate = this.chartBins[pointInGrid[0]];

        //Add the year to the date when emitting
        let date = this.chartBins[pointInGrid[0]];
        if (date.match(/^[a-zA-Z]{3} \d{1,2}$/)?.length) {
          date += " " + new Date().getUTCFullYear();
        }

        const YESTERDAY = dayjs().subtract(1, "days");
        const value = this.chartCounts[pointInGrid[0]];
        if (!dayjs(date).isAfter(YESTERDAY, "day")) {
          this.$emit(EMITTED_ACTIONS.CLICK, {
            date: date,
            value: value,
          });
          this.clickedDate = date;
          this.clickedValue = value;
        } else {
          this.resetSelectedDate();
        }
      }
    },
    resetSelectedDate() {
      this.clickedDate = null;
      this.clickedValue = null;
      this.$emit(EMITTED_ACTIONS.CLICK, { reset: true });
    },
    reloadChart() {
      if (this.$refs.chartInstance) {
        this.$refs.chartInstance.resize();
      }
    },
    exportChart() {
      if (this.$refs.chartInstance) {
        const img = new Image();
        img.src = this.$refs.chartInstance.getDataURL({
          type: "png",
          pixelRatio: 2,
          backgroundColor: "#000000",
        });
        const newTab = window.open();
        newTab.document.body.innerHTML = `<img src="${img.src}" width="1000px">`;
      }
    },
    formatDate(date) {
      return dayjs(date).format("MMM D");
    },
    formatClickedDate(date) {
      return dayjs(date).format("YYYY-MM-DD");
    },
    handleResize() {
      this.reloadChart();
    },
  },
  watch: {
    refresh: {
      handler() {
        this.reloadChart();
      },
    },
    histogram: {
      handler() {
        this.setUpChart();
      },
    },
    stats: {
      immediate: true,
      handler() {
        if (this.type === "stability" && this.stats) {
          this.getLinePieces();
        }
      },
    },
    trend: {
      handler() {
        if (this.type === "stability" && this.trend) {
          this.changeChartTimeframe(this.selectedTimeframe);
        }
      },
    },
    clickClear: {
      handler() {
        if (this.clickClear && this.clickClear !== this.lastClickCleared) {
          this.chartOnClick();
          this.lastClickCleared = this.clickClear;
        }
      },
    },
    forecast: {
      handler() {
        if (this.forecast) {
          this.getBinsAndCounts();
          this.changeChartTimeframe(this.selectedTimeframe);
        }
      },
      deep: true,
    },
    clickedDate: {
      handler() {
        this.setClickedValue();
      },
    },
    preclickedDate: {
      handler() {
        if (this.preclickedDate) {
          this.setClickedValue();
        } else {
          this.resetSelectedDate();
        }
      },
    },
  },
  beforeDestroy() {
    this.bins = null;
    this.counts = null;
    this.chartBins = null;
    this.chartCounts = null;
    this.pieces = null;
    this.forecastBins = null;
    this.forecastCounts = null;
  },
};
</script>

<style scoped>
.hyp-details-chart-text-container {
  color: white;
  font-size: 10px;
}
.hyp-details-chart-text-disabled {
  color: rgba(255, 255, 255, 0.22);
}
.hyp-details-chart-left-side-ball {
  position: absolute;
  left: 4px;
  width: 8px;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.22);
  border-radius: 50%;
}
.hyp-details-chart-left-side-ball-middle {
  top: 120px;
}
.hyp-details-chart-left-side-ball-bottom {
  top: 210px;
}
.icons-container {
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  margin-top: 10px;
  column-gap: 10px;
}
.reload-icon {
  cursor: pointer;
}
.export-icon {
  cursor: pointer;
}
.info-icon {
}
.today-button {
}
</style>
